// Solutions Management Tab
// Date: January 27, 2025

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { contentManagementService, Solution, Template } from '@/services/contentManagementService';
import { toast } from 'react-hot-toast';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';

export const SolutionsTab: React.FC = () => {
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    category: 'generic' as 'generic' | 'industry_specific',
    industry: '',
    marketplace_template_id: '',
    display_order: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [solutionsData, templatesData] = await Promise.all([
        contentManagementService.getAllSolutions(),
        contentManagementService.getMarketplaceTemplates(),
      ]);
      setSolutions(solutionsData.sort((a, b) => a.display_order - b.display_order));
      setTemplates(templatesData.filter(t => t.marketplace_visibility === 'PUBLIC'));
    } catch (error: any) {
      console.error('Error loading solutions:', error);
      toast.error(error.response?.data?.message || 'Failed to load solutions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (formData.category === 'industry_specific' && !formData.industry.trim()) {
      toast.error('Industry is required for industry-specific solutions');
      return;
    }

    try {
      setSaving(true);
      const data = {
        ...formData,
        industry: formData.category === 'industry_specific' ? formData.industry : undefined,
        marketplace_template_id: formData.marketplace_template_id || undefined,
      };
      if (editingId) {
        await contentManagementService.updateSolution(editingId, data);
        toast.success('Solution updated successfully');
      } else {
        await contentManagementService.createSolution(data);
        toast.success('Solution created successfully');
      }
      setShowForm(false);
      setEditingId(null);
      setFormData({
        title: '',
        description: '',
        image_url: '',
        category: 'generic',
        industry: '',
        marketplace_template_id: '',
        display_order: solutions.length,
      });
      await loadData();
    } catch (error: any) {
      console.error('Error saving solution:', error);
      toast.error(error.response?.data?.message || 'Failed to save solution');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (solution: Solution) => {
    setEditingId(solution.id);
    setFormData({
      title: solution.title,
      description: solution.description || '',
      image_url: solution.image_url || '',
      category: solution.category,
      industry: solution.industry || '',
      marketplace_template_id: solution.marketplace_template_id || '',
      display_order: solution.display_order,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this solution?')) {
      return;
    }

    try {
      await contentManagementService.deleteSolution(id);
      toast.success('Solution deleted successfully');
      await loadData();
    } catch (error: any) {
      console.error('Error deleting solution:', error);
      toast.error(error.response?.data?.message || 'Failed to delete solution');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-accent-bg)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">Solutions & Use Cases</h3>
        <Button
          onClick={() => {
            setShowForm(true);
            setEditingId(null);
            setFormData({
              title: '',
              description: '',
              image_url: '',
              category: 'generic',
              industry: '',
              marketplace_template_id: '',
              display_order: solutions.length,
            });
          }}
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Solution
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId ? 'Edit' : 'Add'} Solution</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  Image URL
                </label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  Category *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value as 'generic' | 'industry_specific', industry: '' })}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
                  required
                >
                  <option value="generic">Generic</option>
                  <option value="industry_specific">Industry Specific</option>
                </select>
              </div>
              {formData.category === 'industry_specific' && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                    Industry *
                  </label>
                  <input
                    type="text"
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    placeholder="e.g., Healthcare, Finance"
                    className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  Marketplace Template (optional)
                </label>
                <select
                  value={formData.marketplace_template_id}
                  onChange={(e) => setFormData({ ...formData, marketplace_template_id: e.target.value })}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
                >
                  <option value="">None</option>
                  {templates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-[var(--color-border)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-bg)]"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                    setFormData({
                      title: '',
                      description: '',
                      image_url: '',
                      category: 'generic',
                      industry: '',
                      marketplace_template_id: '',
                      display_order: solutions.length,
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6">
          {solutions.length === 0 ? (
            <p className="text-sm text-[var(--color-secondary-text)] text-center py-4">
              No solutions found. Click &quot;Add Solution&quot; to create one.
            </p>
          ) : (
            <div className="space-y-3">
              {solutions.map((solution) => (
                <div
                  key={solution.id}
                  className="flex items-center justify-between p-4 border border-[var(--color-border)] rounded-lg"
                >
                  <div>
                    <p className="font-medium text-[var(--color-surface-text)]">{solution.title}</p>
                    <p className="text-sm text-[var(--color-secondary-text)]">
                      {solution.category === 'industry_specific' ? `${solution.industry} - ` : ''}
                      {solution.description || 'No description'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(solution)}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(solution.id)}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

